import { ActivityResultType, EventCategory } from '@fluidx/office-fluid-types';
import { NamespaceLoggerAdapter } from './NamespaceLoggerAdapter';
import { redactSharepointDomainAndLoopPageUrl } from './redactDomains';
/**
 * Checks for a defined logger and sends the appropriate telemetry event based on the EventCategory
 * @param eventCategory - the type of event category logging required
 * @param logger - Potential Logger passed to do the logging
 * @param event - Event to send based on the event category selected
 * @param error - Optional ErrorObject for event categories that are capable of logging errors, e.g Error and Activity events
 */ export function tryLogEvent(eventCategory, logger, event, error) {
    if (!logger) {
        return;
    }
    switch(eventCategory){
        case EventCategory.Generic:
            return sendTelemetryEvent(logger, event);
        case EventCategory.Error:
            return sendErrorEvent(logger, event, error);
        case EventCategory.Activity:
            return sendActivityEvent(logger, event, error);
        case EventCategory.UserAction:
            return sendUserActionEvent(logger, event);
    }
}
/**
 * Send generic telemetry event
 * Since the helper has a predefined category, please do not provide a category field in the event
 * @param logger - Logger passed to do the logging
 * @param event - Event to send
 */ export function sendTelemetryEvent(logger, event) {
    logger.send({
        ...event,
        category: EventCategory.Generic
    });
}
/**
 * Send error telemetry event
 * Since the helper has a predefined category, please do not provide a category field in the event
 * @param logger - Logger passed to do the logging
 * @param event - Event to send
 * @param error - Optional Error object passed with the error event.
 */ export function sendErrorEvent(logger, event, error) {
    if (error) {
        prepareErrorObject(event, error);
    }
    logger.send({
        ...event,
        category: EventCategory.Error
    });
}
/**
 * Report user impacting error events in ASHA QoS format.
 * @param logger - Logger passed to do the logging
 * @param event  - ASHA QoS event. This should fully describe the user impact
 * @param error  - Optional error object passed with the error event
 */ export function sendQoSErrorEvent(logger, event, error) {
    sendErrorEvent(logger, event, error);
    forkQoSErrorEvents(logger, event, error);
}
/**
 * Override logger namespace to bunch all QoSErrors into a single namespace 'QoS20'
 * This is temporary while we investigate the ideal namespacing pattern to use for ASHA QoS events
 */ function forkQoSErrorEvents(logger, event, error) {
    const qosNamespaceLogger = new NamespaceLoggerAdapter('QoS20', logger);
    sendErrorEvent(qosNamespaceLogger, event, error);
}
const loggedDeprecatedEvents = new Set();
const loggedStack = new Set();
const thresholdAudienceMapping = new Map([
    [
        'Automation',
        1
    ],
    [
        'SyntheticTraffic',
        1
    ],
    [
        'Local',
        1
    ],
    [
        'Staging',
        1
    ],
    [
        'Dogfood',
        0.1
    ],
    [
        'Preview',
        0.1
    ],
    [
        'Microsoft',
        0.01
    ],
    [
        'Production',
        0.001
    ],
    [
        'GCC',
        0
    ],
    [
        'GCC-High',
        0
    ],
    [
        'DoD',
        0
    ]
]);
/**
 * Send telemetry event for deprecated code
 * Since the helper has a predefined category, please do not provide a category field in the event
 * @param logger - Logger passed to do the logging
 * @param event - Event to send
 */ export function sendDeprecatedEvent(logger, event, audience) {
    // The threshold approach is taken to further reduce the volume of deprecated events logged
    const threshold = audience ? thresholdAudienceMapping.get(audience) : 0;
    if (!threshold || Math.random() > threshold) {
        return;
    }
    // Creates a new table: Office_Fluid_Deprecated_Generic that incorporates all deprecated activities
    const namespaceLogger = new NamespaceLoggerAdapter('Deprecated', logger);
    if (!event.stack) {
        event.stack = getCallStack();
    }
    // Redact to make sure no domain info is logged
    if (event.stack) {
        event.stack = redactSharepointDomainAndLoopPageUrl(event.stack.toString());
    }
    if (loggedDeprecatedEvents.has(event.eventName) && loggedStack.has(event.stack)) {
        // If we have already logged before, early return to reduce telemetry volume
        return;
    }
    sendTelemetryEvent(namespaceLogger, event);
    loggedDeprecatedEvents.add(event.eventName);
    loggedStack.add(event.stack);
}
/**
 * Send activity telemetry event
 * Since the helper has a predefined category, please do not provide a category field in the event
 * @param logger - Logger passed to do the logging
 * @param event - Event to send
 * @param error - Optional Error object passed with the activity event in case of activity failure
 */ export function sendActivityEvent(logger, event, error) {
    if (error) {
        prepareErrorObject(event, error);
    }
    // If the callers do not provide resultType, use succeeded if present to derive the resultType
    if (!event.resultType && event.succeeded !== undefined) {
        event.resultType = event.succeeded ? ActivityResultType.Success : ActivityResultType.Failure;
    }
    logger.send({
        ...event,
        category: EventCategory.Activity
    });
}
/**
 * Send User Action telemetry event
 * Since the helper has a predefined category, please do not provide a category field in the event
 * @param logger - Logger passed to do the logging
 * @param event - Event to send
 */ export function sendUserActionEvent(logger, event) {
    logger.send({
        ...event,
        category: EventCategory.UserAction
    });
}
/** Extract some common properties from the given unknown error object
 * and apply them to the event, overwriting existing values
 */ /* eslint-disable-next-line @typescript-eslint/no-explicit-any
-- TODO: Using 'any' type defeats the purpose of TypeScript. Consider replacing it with a concrete type, or 'unknown'. */ function applyCommonErrorPropertiesToEvent(error, event) {
    const isErrorObject = typeof error === 'object' && error !== null;
    function extractStringFromError(propertyName) {
        if (!isErrorObject) {
            return undefined;
        }
        const propertyValue = error[propertyName];
        return typeof propertyValue === 'string' ? propertyValue : undefined;
    }
    event.errorMessage = extractStringFromError('message') ?? String(error);
    event.errorName = extractStringFromError('name');
    event.stack = extractStringFromError('stack');
    event.errorType = extractStringFromError('errorType');
    event.errorInstanceId = extractStringFromError('errorInstanceId');
}
function prepareErrorObject(event, error) {
    // WARNING: Exceptions can contain PII!
    // For example, XHR will throw object derived from Error that contains config information
    // for failed request, including all the headers, and thus - user tokens!
    // Extract only message, call stack, and errorType.
    // Note: This will overwrite existing values on the event
    applyCommonErrorPropertiesToEvent(error, event);
    if (event.message) {
        event.message = `${event.message}\n${event.errorMessage}`;
    } else {
        event.message = event.errorMessage;
    }
    // Collect stack if we were not able to extract it from error
    if (event.stack === undefined) {
        event.stack = getErrorCallStack();
    }
    // Redact any domain information
    if (event.errorMessage) {
        event.errorMessage = redactSharepointDomainAndLoopPageUrl(event.errorMessage.toString());
    }
    if (event.message) {
        event.message = redactSharepointDomainAndLoopPageUrl(event.message.toString());
    }
    if (event.stack) {
        event.stack = redactSharepointDomainAndLoopPageUrl(event.stack.toString());
    }
    if (event.error) {
        event.error = redactSharepointDomainAndLoopPageUrl(event.error.toString());
    }
    if (event.responseMessage) {
        event.responseMessage = redactSharepointDomainAndLoopPageUrl(event.responseMessage.toString());
    }
}
export function getErrorCallStack() {
    // Some browsers will populate stack right away, others require throwing Error
    let stack = new Error().stack;
    if (!stack) {
        try {
            throw new Error();
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any
-- TODO: Using 'any' type defeats the purpose of TypeScript. Consider replacing it with a concrete type, or 'unknown'. */ } catch (e) {
            stack = e.stack;
        }
    }
    return stack;
}
export function getCallStack() {
    let callStack = getErrorCallStack();
    if (callStack?.startsWith('Error')) {
        // removing 'Error' from stack
        callStack = callStack.substring(5);
    }
    return callStack;
}
